import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, ExternalLink, RefreshCw } from 'lucide-react';
import './DiologicSchedulerEmbed.css';

const DEFAULT_SCHEDULER_URL = 'http://localhost:3100';

function buildEmbedUrl(base) {
  try {
    const url = new URL(base);
    url.searchParams.set('embed', '1');
    return url.toString();
  } catch {
    const trimmed = String(base || '').replace(/\/$/, '');
    return `${trimmed || DEFAULT_SCHEDULER_URL}?embed=1`;
  }
}

/**
 * Embeds the standalone Diologic Scheduler (Supabase app) inside the portal Calendar page.
 */
function DiologicSchedulerEmbed() {
  const baseUrl = process.env.REACT_APP_SCHEDULER_URL || DEFAULT_SCHEDULER_URL;
  const embedUrl = useMemo(() => buildEmbedUrl(baseUrl), [baseUrl]);
  const [failed, setFailed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    setFailed(false);

    const probe = async () => {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);
        await fetch(baseUrl, { mode: 'no-cors', signal: controller.signal });
        clearTimeout(timer);
        if (!cancelled) {
          setFailed(false);
          setChecking(false);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
          setChecking(false);
        }
      }
    };

    probe();
    return () => { cancelled = true; };
  }, [baseUrl, reloadKey]);

  const handleRetry = () => {
    setFailed(false);
    setChecking(true);
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="scheduler-embed">
      <div className="scheduler-embed-header">
        <div>
          <h1>Calendar</h1>
          <p>Diologic Scheduler — bookings, restrictions, and reports</p>
        </div>
        <div className="scheduler-embed-actions">
          <button type="button" className="scheduler-embed-btn" onClick={handleRetry} title="Reload scheduler">
            <RefreshCw size={16} />
            Reload
          </button>
          <a
            className="scheduler-embed-btn scheduler-embed-btn-secondary"
            href={baseUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={16} />
            Open full app
          </a>
        </div>
      </div>

      {checking ? (
        <div className="scheduler-embed-fallback">
          <div className="loading-spinner" />
          <p>Connecting to Diologic Scheduler…</p>
        </div>
      ) : failed ? (
        <div className="scheduler-embed-fallback">
          <Calendar size={40} className="scheduler-embed-fallback-icon" />
          <h3>Scheduler is not reachable</h3>
          <p>
            Start the Diologic Scheduler server, then reload this page.
          </p>
          <code className="scheduler-embed-cmd">cd diologic-scheduler &amp;&amp; npm start</code>
          <p className="scheduler-embed-url">Expected at <strong>{baseUrl}</strong></p>
          <button type="button" className="scheduler-embed-btn" onClick={handleRetry}>
            <RefreshCw size={16} />
            Try again
          </button>
        </div>
      ) : (
        <iframe
          key={reloadKey}
          className="scheduler-embed-frame"
          title="Diologic Scheduler"
          src={embedUrl}
        />
      )}
    </div>
  );
}

export default DiologicSchedulerEmbed;
